import { useEffect, useMemo, useRef, useState } from "react";
import type { Catalog, SeasonKey, Song } from "../types";
import { loadSongTitles, matchSongs, type SongOption, type CarnetIndex } from "../lib/songIndex";
import { loadCarnet } from "../lib/carnet";

export interface CarnetHit { page: string; ref?: string }

/**
 * Typeahead for a song title. As you type it autocompletes across the curated
 * catalog AND the lyrics library; an empty field shows the moment's suggestions.
 * Free text is always allowed (the typed value is the title).
 */
export function SongAutocomplete({
  value,
  onPick,
  catalog,
  moment,
  season,
  placeholder = "Rechercher ou saisir un chant…",
}: {
  value: string;
  onPick: (title: string, song?: Song, carnet?: CarnetHit) => void;
  catalog: Catalog;
  moment: string;
  season: SeasonKey;
  placeholder?: string;
}) {
  const [titles, setTitles] = useState<string[]>([]);
  const [carnet, setCarnet] = useState<CarnetIndex | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadSongTitles().then(setTitles); }, []);
  useEffect(() => { loadCarnet().then(setCarnet); }, []);

  // Recompute matches from the current input value + the loaded indices.
  const options = useMemo(
    () => matchSongs(value, catalog, titles, moment, season, 10, carnet),
    [value, catalog, titles, moment, season, carnet]
  );

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Keep the active option scrolled into view.
  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function choose(opt: SongOption) {
    onPick(opt.title, opt.song, opt.carnetPage ? { page: opt.carnetPage, ref: opt.carnetRef } : undefined);
    setOpen(false);
    setActive(-1);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") { e.preventDefault(); if (!open) setOpen(true); setActive((i) => Math.min(i + 1, options.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { if (open && active >= 0 && options[active]) { e.preventDefault(); choose(options[active]); } }
    else if (e.key === "Escape") { setOpen(false); setActive(-1); }
  }

  const [focus, setFocus] = useState(false);
  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--bg-raised)", border: `1px solid ${focus ? "var(--accent)" : "var(--border-strong)"}`, borderRadius: "var(--radius-md)", padding: "0 12px", boxShadow: focus ? "0 0 0 var(--ring-width) var(--focus-ring)" : "none", transition: "border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out)" }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          value={value}
          placeholder={placeholder}
          onChange={(e) => { onPick(e.target.value); setOpen(true); setActive(-1); }}
          onFocus={() => { setFocus(true); setOpen(true); }}
          onBlur={() => setFocus(false)}
          onKeyDown={onKeyDown}
          style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontFamily: "var(--font-ui)", fontSize: "var(--text-base)", color: "var(--text-primary)", padding: "10px 0", minWidth: 0 }}
        />
        {value && (
          <button type="button" aria-label="Effacer" onMouseDown={(e) => { e.preventDefault(); onPick(""); setOpen(true); }} style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, marginRight: -6, border: "none", background: "transparent", color: "var(--text-muted)", cursor: "pointer", padding: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        )}
      </div>

      {open && options.length > 0 && (
        <div ref={listRef} role="listbox" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 50, maxHeight: 280, overflowY: "auto", background: "var(--bg-raised)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-lg)", padding: 4, animation: "adm-pop var(--dur-base) var(--ease-out)" }}>
          {options.map((opt, i) => (
            <div
              key={opt.title + i}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "8px 10px", borderRadius: "var(--radius-sm)", cursor: "pointer", background: i === active ? "var(--accent-soft)" : "transparent" }}
            >
              <span style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-base)", color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{opt.title}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                {opt.carnetPage ? <CarnetChip page={opt.carnetPage} cat={opt.carnetCat} /> : (!opt.song && <Tag muted>paroles</Tag>)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// "carnet" chip — the song is in the parish songbook; shows its page so picking it fills it.
function CarnetChip({ page, cat }: { page: string; cat?: string }) {
  return (
    <span title={`Dans le carnet de chant — page ${page}${cat ? ` · ${cat}` : ""}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontWeight: 700, color: "#8a5a1a", background: "#fbf1de", border: "1px solid #e7cfa3", borderRadius: "var(--radius-pill)", padding: "1px 7px", lineHeight: 1.4, whiteSpace: "nowrap" }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
      carnet p. {page}
    </span>
  );
}

function Tag({ children, muted = false }: { children: React.ReactNode; muted?: boolean }) {
  return (
    <span style={{ fontFamily: "var(--font-ui)", fontSize: "var(--text-xs)", fontWeight: 600, color: muted ? "var(--text-faint)" : "var(--accent-text)", background: muted ? "var(--bg-sunken)" : "var(--accent-soft)", border: `1px solid ${muted ? "var(--border)" : "var(--accent-ring)"}`, borderRadius: "var(--radius-pill)", padding: "1px 7px", whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}
